<template>
    <div>
        <ValidationObserver ref="formValidate">
            <b-row>
                <b-col cols="12" md="6">
                    <ValidationProvider name="semester" rules="required" v-slot="{ valid, errors }">
                        <b-form-group :label="$t('semester')">
                            <semesters-selectbox v-model="form.semester_id"
                                                 :setActive="true"
                                                 disabled
                                                 :validate-error="errors[0]"></semesters-selectbox>
                        </b-form-group>
                    </ValidationProvider>
                </b-col>
                <b-col cols="12" md="6">
                    <b-form-group :label="$t('teaching_staff')">
                        <ValidationProvider name="instructor_id" rules="required" v-slot="{ errors }">
                            <staff-auto-complete v-model="form.instructor_id"
                                                 :valueObject="instructorValueObject"
                                                 disabled
                                                 :validate-error="errors[0]" />
                        </ValidationProvider>
                    </b-form-group>
                </b-col>
                <b-col cols="12">
                    <b-form-group :label="$t('course')">
                        <div class="label-as-input font-weight-bold" v-if="course">
                            {{ course.code }} - {{ getLocaleText(course, 'name') }}
                        </div>
                    </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                    <ValidationProvider name="from_date" rules="required" v-slot="{ valid, errors }">
                        <b-form-group :label="$t('from_date')">
                            <b-form-input type="datetime-local" v-model="form.from_date"></b-form-input>
                            <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"></b-form-invalid-feedback>
                        </b-form-group>
                    </ValidationProvider>
                </b-col>
                <b-col cols="12" md="6">
                    <ValidationProvider name="to_date" rules="required" v-slot="{ valid, errors }">
                        <b-form-group :label="$t('to_date')">
                            <b-form-input type="datetime-local" v-model="form.to_date"></b-form-input>
                            <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"></b-form-invalid-feedback>
                        </b-form-group>
                    </ValidationProvider>
                </b-col>
                <b-col cols="12" class="mt-2 mb-2">
                    <ValidationProvider name="content" rules="required" v-slot="{valid, errors}">
                        <b-form-group :label="$t('content')">
                            <ck-editor v-model="form.content"/>
                            <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]" />
                        </b-form-group>
                    </ValidationProvider>
                </b-col>
                <b-col cols="12">
                    <ValidationProvider name="section_ids" rules="required" v-slot="{ errors }">
                        <b-form-group :label="$t('section')">
                            <multi-selectbox v-model="form.section_ids"
                                             :options="sectionOptions"
                                             :multiple="true"
                                             :validate-error="errors[0]" />
                        </b-form-group>
                    </ValidationProvider>
                </b-col>
            </b-row>
        </ValidationObserver>
        <b-button variant="primary" @click="save">{{ $t('save').toUpper() }}</b-button>
    </div>
</template>
<script>

import LmsAnnouncementsService from "@/services/LmsAnnouncementsService";
import SectionService from "@/services/SectionService";
import moment from "moment/moment";
import CkEditor from "@/components/interactive-fields/Ckeditor.vue";
import MultiSelectbox from "@/components/interactive-fields/MultiSelectbox.vue";

export default {
    components: {
        MultiSelectbox,
        CkEditor
    },
    props: {
        semesterId: {},
        formId: {
            type: Number
        },
        course: {
            type: Object
        },
    },
    data() {
        return {
            form: {
                content: null,
                instructor_id: null,
                from_date: null,
                to_date: null,
                section_ids: [],
            },
            instructorValueObject: {},
            sectionOptions: []
        }
    },
    created() {
        this.getSections()
        this.getData()
    },
    methods: {
        getSections() {
            this.sectionOptions = []
            const courseId = this.course ? this.course.id : this.form.course_id
            const semesterId = this.semesterId || this.form.semester_id
            if (!courseId || !semesterId) return

            const config = {
                params: {
                    course_id: courseId,
                    semester_id: semesterId
                },
            }
            SectionService.instructorSections(config).then(response => {
                if (response.data && response.data.data) {
                    response.data.data.forEach(item => {
                        this.sectionOptions.push({
                            value: item.section_id,
                            text: item.section
                        })
                    })
                    this.sectionOptions.sort((a, b) => a.text - b.text)
                }
            }).catch(e => {
                console.error(e)
            })
        },
        getData(){
            LmsAnnouncementsService.show(this.formId)
                .then(response => {
                    let data = response.data.data
                    this.instructorValueObject = {
                        value: data.instructor_id,
                        text: data.instructor_name + ' - ' + data.instructor_surname
                    }
                    this.form = {
                        content: data.content ? this.decodeHTMLEntities(data.content) : null,
                        course_id: data.course_id,
                        to_date: data.to_date,
                        from_date: data.from_date,
                        instructor_id: data.instructor_id,
                        section_ids: data.section_ids || [],
                        semester_id: data.semester_id,
                    }
                    if (!this.sectionOptions.length) {
                        this.getSections()
                    }
                }).catch(err => {
                this.showErrors(err)
            })

        },
        async save() {
            const isValid = await this.$refs.formValidate.validate();
            if (isValid){
                const fromDate = moment(this.form.from_date).format('YYYY-MM-DD HH:mm:ss')
                const toDate   = moment(this.form.to_date).format('YYYY-MM-DD HH:mm:ss')

                const sectionIds = Array.isArray(this.form.section_ids)
                    ? this.form.section_ids
                    : (this.form.section_ids ? [this.form.section_ids] : [])

                const payload = {
                    semester_id: this.semesterId || this.form.semester_id,
                    course_id: this.form.course_id,
                    instructor_id: this.form.instructor_id,
                    content: this.form.content,
                    from_date: fromDate,
                    to_date: toDate,
                    section_ids: sectionIds
                }

                LmsAnnouncementsService.update(this.formId, payload)
                    .then(response => {
                        this.$toast.success(response.data.message)
                        this.$emit('announcementSuccess', true)

                    }).catch(err => {
                    this.showErrors(err)
                })
            }

        }
    },

};
</script>
